/**
 * BestpreisFloatingBanner: dezenter Sticky-CTA, der den User über alle
 * Public-Seiten begleitet.
 *
 * Verhalten:
 *  - fixed bottom-right (Desktop) / bottom-center (Mobile)
 *  - Erscheint nach 1.5s (fade + slide up)
 *  - Puls-Animation alle 12s (subtil, nicht nervend)
 *  - Kollabierbar: X-Klick → 6h Cooldown im localStorage
 *  - Versteckt sich auf: /bestpreisgarantie, /admin/*, /login, /registrieren, /kasse, /checkout
 *  - Weicht dem Cookie-Consent aus (bottom-24 wenn Consent noch da)
 */
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { m as motion, AnimatePresence } from "framer-motion";
import { Percent, X, ArrowRight } from "lucide-react";
import { useBestpreisAnchor } from "@/components/BestpreisWidgets";

const LS_KEY = "bp_banner_dismissed_at";
const COOLDOWN_MS = 6 * 60 * 60 * 1000; // 6 h
const HIDE_PREFIXES = ["/bestpreisgarantie", "/bestpreis", "/admin", "/login", "/registrieren", "/kasse", "/en", "/nl"];
// IDs von Sektionen, in denen der Banner NICHT über den Inhalten liegen darf
const SUPPRESS_WHEN_IN_VIEW = ["finanzierung", "amortisation", "software-vergleich"];

export default function BestpreisFloatingBanner() {
  const location = useLocation();
  const openForm = useBestpreisAnchor();
  const [visible, setVisible] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [collapsed, setCollapsed] = useState(false); // mobile tab-mode

  const hideOnRoute = HIDE_PREFIXES.some((p) => location.pathname.startsWith(p));

  // Kevin 01.11.2026: nur ab und zu für 10 Sekunden, schmal mittig UNTER dem Chat-Knopf (der rutscht dafür hoch).
  // Rhythmus: erstmals nach 30 s, danach alle 3 Minuten, höchstens 3-mal je Besuch; nie gleichzeitig mit der Chat-Sprechblase.
  useEffect(() => {
    if (hideOnRoute) { setVisible(false); return undefined; }
    const dismissedAt = Number(localStorage.getItem(LS_KEY) || "0");
    if (Date.now() - dismissedAt < COOLDOWN_MS) return undefined;
    let timers = [];
    const shown = () => Number(sessionStorage.getItem("bp_shown") || "0");
    const zeige = () => {
      if (shown() >= 3) return;
      if (document.documentElement.hasAttribute("data-fbv-blase") || document.hidden) { timers.push(setTimeout(zeige, 20000)); return; }
      sessionStorage.setItem("bp_shown", String(shown() + 1));
      setVisible(true);
      timers.push(setTimeout(() => setVisible(false), 10000));
      timers.push(setTimeout(zeige, 180000));
    };
    timers.push(setTimeout(zeige, 30000));
    return () => timers.forEach(clearTimeout);
  }, [hideOnRoute, location.pathname]);

  // Beim Scroll in Formulare/Rechner automatisch minimieren, nur mobile
  const visibleMap = useRef({});
  useEffect(() => {
    if (!visible) return;
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (!isMobile) return;

    visibleMap.current = {};

    const recompute = () => {
      const anyVisible = Object.values(visibleMap.current).some(Boolean);
      setCollapsed((prev) => (prev === anyVisible ? prev : anyVisible));
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const id = e.target?.id;
          if (!id) continue;
          visibleMap.current[id] = e.isIntersecting;
        }
        recompute();
      },
      { threshold: 0.15, rootMargin: "0px 0px -20% 0px" }
    );

    const collect = setTimeout(() => {
      SUPPRESS_WHEN_IN_VIEW.forEach((id) => {
        const el = document.getElementById(id);
        if (!el) return;
        // Initial-State direkt aus dem Layout ableiten (deckt bereits gescrollte Positionen ab)
        const rect = el.getBoundingClientRect();
        const inView = rect.top < window.innerHeight * 0.8 && rect.bottom > window.innerHeight * 0.2;
        visibleMap.current[id] = inView;
        observer.observe(el);
      });
      recompute();
    }, 200);

    return () => { clearTimeout(collect); observer.disconnect(); };
  }, [visible, location.pathname]);

  useEffect(() => {
    if (!visible || collapsed) return;
    const iv = setInterval(() => {
      setPulse(true);
      setTimeout(() => setPulse(false), 1400);
    }, 12000);
    return () => clearInterval(iv);
  }, [visible, collapsed]);

  // Kevin 01.11.: Chat-Knopf oben, Pille darunter. Der Chat-Knopf rutscht um diese Höhe nach oben.
  useEffect(() => {
    const de = document.documentElement;
    if (visible && !hideOnRoute) de.setAttribute("data-fbv-bp", collapsed ? "40" : "34");
    else de.removeAttribute("data-fbv-bp");
    return () => de.removeAttribute("data-fbv-bp");
  }, [visible, collapsed, hideOnRoute]);

  const dismiss = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    localStorage.setItem(LS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (hideOnRoute) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="bp-float"
          data-testid="bestpreis-floating"
          initial={{ x: "-50%", y: 20, opacity: 0 }}
          animate={{ x: "-50%", y: 0, opacity: 1 }}
          exit={{ x: "-50%", y: 20, opacity: 0 }}
          transition={{ type: "spring", stiffness: 220, damping: 24 }}
          className="contrast-dark fixed z-40 pointer-events-none"
          style={{ left: "50%", bottom: "var(--fbv-chat-unten, 18px)" }}
        >
          {collapsed ? (
            // Mobile Tab-Mode: kleines rundes Icon, verdeckt keine Inhalte
            <button
              type="button"
              onClick={() => setCollapsed(false)}
              data-testid="bestpreis-floating-collapsed"
              aria-label="Bestpreis-Info anzeigen"
              className="pointer-events-auto w-10 h-10 rounded-full flex items-center justify-center shadow-lg"
              style={{
                background: "linear-gradient(135deg, #14120F 0%, #221C15 55%, #2E2317 100%)",
                border: "1px solid rgba(232,168,61,0.55)",
              }}
            >
              <Percent className="w-4 h-4 text-brand-yellow" strokeWidth={2.6}/>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => openForm()}
              data-testid="bestpreis-floating-cta"
              className="group pointer-events-auto relative flex items-center justify-center gap-1.5 pl-1.5 pr-1 rounded-full overflow-hidden text-left"
              style={{
                height: 34, whiteSpace: "nowrap",
                background: "linear-gradient(135deg, #14120F 0%, #221C15 55%, #2E2317 100%)",
                border: "1px solid rgba(232,168,61,0.55)",
                boxShadow: pulse
                  ? "0 20px 40px -12px rgba(232,168,61,0.55), 0 0 0 6px rgba(232,168,61,0.12)"
                  : "0 12px 28px -12px rgba(20,18,15,0.28)",
              }}
            >
              <span aria-hidden className="pointer-events-none absolute inset-0"
                style={{ background: "radial-gradient(80% 100% at 100% 50%, rgba(244,184,74,0.22) 0%, transparent 60%)" }} />

              <motion.span
                aria-hidden
                animate={pulse ? { scale: [1, 1.15, 1] } : { scale: 1 }}
                transition={{ duration: 1.2, ease: "easeInOut" }}
                className="relative w-6 h-6 rounded-full flex items-center justify-center shrink-0"
                style={{ background: "rgba(244,184,74,0.18)", border: "1px solid rgba(244,184,74,0.5)" }}
              >
                <Percent className="w-3 h-3 text-brand-yellow" strokeWidth={2.4} />
              </motion.span>

              <div className="relative min-w-0 text-white text-[12px] leading-tight whitespace-nowrap">
                <b className="text-brand-yellow font-semibold">Bestpreisgarantie</b><span className="hidden sm:inline text-white/80"> · wir legen nach</span>
              </div>

              <ArrowRight className="relative w-3.5 h-3.5 text-brand-yellow shrink-0 transition-transform group-hover:translate-x-0.5" />

              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); dismiss(); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); dismiss(); } }}
                aria-label="Bestpreis-Banner schließen"
                data-testid="bestpreis-floating-close"
                className="relative w-6 h-6 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors shrink-0 cursor-pointer"
              >
                <X className="w-3.5 h-3.5" />
              </span>
            </button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
